import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ReactVirtualized from './index';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';
import { TableCell } from '@material-ui/core';
import classNames from 'classnames/bind';
import SortIndicator from './SortIndicator';
import { useMediaQuery } from '@mui/material';
import { convertToTwoDecimal } from '../../../helper/convertAmount';
import sortActive from '../../../assets/dashboard-svg/sort-active.svg';
import sortInactive from '../../../assets/dashboard-svg/sort-inactive.svg';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { colors } from '../../../theme/color-palette';
import { styled } from '@mui/material/styles';
import ColumnGroupingTable from '../DrilldownMuiTable/ColumnGroupingTable.js';
import { getTopBottomGrid } from '../../../redux/actions/itemDrillDownActions';

const { POSITIVE_GROWTH_COLOR, NEGATIVE_GROWTH_COLOR } = colors;
const { Column, Table, SortDirection, CellMeasurerCache, CellMeasurer, List } =
	ReactVirtualized;

const StyledTable = styled(Table)`
	.ReactVirtualized__Table__row {
		:hover {
			background: #f9f9f9;
		}
	},
`;

const ellipsMaxWidth = (kpiName) =>
	kpiName !== 'InStockInventory' ? '150px' : '300px';
const useStyles = makeStyles((theme) => ({
	flexContainer: {
		display: 'flex',
		alignItems: 'center',
	},
	table: {
		'& .ReactVirtualized__Table__Grid': {
			overflowY: 'hidden !important',
			'&:hover': {
				overflowY: 'scroll !important',
			},
		},
		'& :focus': {
			outline: 1,
		},
	},
	rows: {
		borderBottom: '1px solid #DADCE0',
		'&.ReactVirtualized__Table__headerRow': {
			borderBottom: '0px',
		},
	},
	header: {
		color: '#a8b7e9',
		borderBottom: '0px',
	},
	tableHeader: {
		textTransform: 'none',
		fontFamily: 'Bogle-Medium',
		fontSize: '14px',
		color: '#6D6E71',
	},
	tableCellFonts: {
		fontFamily: 'Bogle-Regular',
		fontSize: '16px',
		cursor: 'pointer',
		textAlign: 'right',
	},
	tableCell: {
		borderBottom: 'none',
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
	},
	ellipsis: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maxWidth: (P) =>
			P.isNotSmallScreen || P.isSmallScreen
				? `${ellipsMaxWidth(P.kpiName)}`
				: '100px',
	},
	firstCoumnEllipsis: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maxWidth: (P) =>
			P.isNotSmallScreen
				? P.isMediumScreen
					? '150px'
					: '100px'
				: P.isSmallScreen
				? `${ellipsMaxWidth(P.kpiName)}`
				: '100px',
	},
	list: {
		width: '100%',
	},
	listCell: {
		borderBottom: '1px solid #DADCE0',
	},
	smallScreenFirstCol: {
		padding: '13px 0px 4px 16px',
		textAlign: 'left',
	},
	smallScreenSecondCol: {
		display: 'flex',
		padding: '0px 16px 0px 16px',
		textAlign: 'left',
		position: 'relative',
		width: 'calc(100vw - 64px)',
	},
	positiveChange: {
		position: 'absolute',
		bottom: 12,
		right: 16,
		color: POSITIVE_GROWTH_COLOR,
	},
	negativeChange: {
		position: 'absolute',
		bottom: 12,
		right: 16,
		color: NEGATIVE_GROWTH_COLOR,
	},
	arrow: {
		fontSize: 'large',
		marginBottom: '-3px',
		marginRight: '3px',
	},
}));

/**
 * Table component that enables virtualized listing of data
 */
const VirtualizedTable = (props) => {
	const location = useLocation();
	const isNotSmallScreen = useMediaQuery('(min-width:700px)');
	const isSmallScreen = useMediaQuery('(min-width:540px)');
	const isMediumScreen = useMediaQuery('(min-width:960px)');
	const isItemPath = location?.pathname === '/drilldown/store';
	const dispatch = useDispatch();
	const kpiName = useSelector((state) => state.salesReducer.kpiName);
	const classes = useStyles({
		isNotSmallScreen,
		isSmallScreen,
		isMediumScreen,
		kpiName,
	});
	const cx = classNames;
	const {
		columns,
		rowHeight,
		smallScreenRowHeight,
		headerHeight,
		data,
		height,
		width,
		initialSortKey,
		handleRowClick,
		externalSortClick,
		isOnHandInventory,
		isItemTab,
		valueUnit,
		selectedTab,
		isTabItem,
		isFourColumns,
		fontSize,
		paddingRight,
		paddingLeft,
		subHeaders,
		marginLeft,
		numberOfRecords,
		isStore,
		...tableProps
	} = props;

	const [sortByDataKey, setSortByDataKey] = useState(initialSortKey);
	const [sortedList, setSortedList] = useState(
		isItemPath ? data?.topList : data
	);
	const [dataSortDirection, setDataSortDirection] = useState(
		SortDirection.DESC
	);

	const cache = new CellMeasurerCache({
		fixedWidth: true,
		minHeight: 25,
	});

	useEffect(() => {
		isItemPath ? setSortedList(data?.topList) : setSortedList(data);
	}, [data]);

	/**
	 * Function that returns the sorted list based on sort key and sort direction
	 * @param {string} sortBy - Key, based on which the data is to be sorted
	 * @param {string} sortDirection - Direction in which the data has to be sorted
	 * @returns {Array} - Sorted list
	 */
	const sortList = ({ sortBy, sortDirection }) => {
		// item level grids in Location
		if (isItemPath && sortBy === initialSortKey) {
			return sortDirection !== SortDirection.DESC
				? data?.bottomList
				: data?.topList;
		} else {
			const orderedSortedList = orderBy(sortedList, [
				(sortedData) => {
					if (sortedData[sortBy] === 'NA') {
						return Number.NEGATIVE_INFINITY;
					} else if (isNaN(Number(sortedData[sortBy]))) {
						return sortedData[sortBy]?.toLowerCase();
					} else {
						return Number(sortedData[sortBy]);
					}
				},
			]);

			if (sortDirection === SortDirection.DESC) {
				orderedSortedList.reverse();
			}
			return orderedSortedList;
		}
	};

	useEffect(() => {
		sort({
			sortBy: initialSortKey || (isOnHandInventory ? 'inventory' : 'sales'),
			sortDirection: externalSortClick ? 'DESC' : 'ASC',
		});
	}, [externalSortClick]);

	/**
	 * Function that sorts data based on sort key and sort direction and sets state
	 * @param {string} sortBy - Key, based on which the data is to be sorted
	 * @param {string} sortDirection - Direction in which the data has to be sorted
	 */
	const sort = ({ sortBy, sortDirection }) => {
		if (
			isTabItem &&
			(sortBy === 'inventory' ||
				sortBy === 'onOrderAMTTy' ||
				sortBy === 'sales')
		) {
			setSortByDataKey(sortBy);
			setDataSortDirection(sortDirection);
			dispatch(getTopBottomGrid(sortDirection));
			return;
		}
		const newSortedList = sortList({ sortBy, sortDirection });
		setSortByDataKey(sortBy);
		setDataSortDirection(sortDirection);
		setSortedList(newSortedList);
		if (initialSortKey === sortBy) {
			dispatch(getTopBottomGrid(sortDirection));
		}
	};

	/**
	 * Function that is supplied to cellRenderer prop that defines how a cell's content is to be rendered
	 * @param {Object} cellProperties -  Object containing the list of properties related to a table cell
	 * @returns {Component} - Table cell to be rendered
	 */
	const cellRenderer = (cellProperties) => {
		const { index, cellData, parent, rowData, rowIndex, columnIndex, dataKey } =
			cellProperties;

		return (
			<CellMeasurer
				cache={cache}
				key={`tableCells-${index}`}
				columnIndex={columnIndex}
				parent={parent}
				style={{ borderBottom: '1px solid black' }}
				rowIndex={rowIndex}>
				<TableCell
					component="div"
					className={cx(
						classes.tableCell,
						classes.flexContainer,
						classes.tableCellFonts
					)}
					role={'gridcell'}
					onClick={() => handleRowClick(rowData)}
					variant="body"
					align="left">
					<span
						className={[
							classes.ellipsis,
							columnIndex == 0 ? classes.firstCoumnEllipsis : '',
						]}>
						{dataKey === 'dimension'
							? Number(cellData)
							: cellData?.toLocaleString()}
					</span>
				</TableCell>
			</CellMeasurer>
		);
	};

	const rowRenderer = (cellProperties) => {
		const { index, parent, rowIndex, columnIndex, key, style } = cellProperties;
		const cellData = isItemTab ? data[index] : sortedList[index];
		const check = Math.sign(cellData['change']) !== -1;
		return (
			<CellMeasurer
				cache={cache}
				key={`listItems-${key}`}
				columnIndex={columnIndex}
				parent={parent}
				style={{ borderBottom: '1px solid' }}
				rowIndex={rowIndex}>
				<TableCell
					component="div"
					className={cx(
						classes.tableCell,
						classes.flexContainer,
						classes.tableCellFonts,
						classes.listCell
					)}
					role="rowgroup"
					onClick={() => handleRowClick(cellData)}
					variant="body"
					align="left"
					key={key}
					style={style}>
					<div>
						<div className={classes.smallScreenFirstCol}>
							{cellData['firstColumn']}
						</div>
						<div className={classes.smallScreenSecondCol}>
							<div>{convertToTwoDecimal(cellData['sales'])}</div>
							<div
								className={
									check ? classes.positiveChange : classes.negativeChange
								}>
								{check ? (
									<ArrowUpwardIcon className={classes.arrow} />
								) : (
									<ArrowDownwardIcon className={classes.arrow} />
								)}
								{`${convertToTwoDecimal(cellData['change'])}%`}
							</div>
						</div>
					</div>
				</TableCell>
			</CellMeasurer>
		);
	};

	/**
	 * Function that is supplied to headerRenderer prop that defines how a header cell's content is to be rendered
	 * @param {Object} headerProperties -  Object containing the list of properties related to a table header cell
	 * @returns {Component} - Table header cell to be rendered
	 */
	const headerRenderer = (headerProperties) => {
		const { label, dataKey, disableSort, sortBy, sortDirection } =
			headerProperties;
		const sortKey = sortBy === dataKey ? sortDirection : 'DESC';
		return (
			<TableCell
				component="div"
				className={cx(
					classes.flexContainer,
					classes.tableCell,
					classes.tableHeader
				)}
				role="columnheader"
				variant="head"
				style={{ height: headerHeight }}
				align="left">
				{label}
				{!disableSort && sortByDataKey === dataKey ? (
					<SortIndicator indicatorIcon={sortActive} sortDirection={sortKey} />
				) : (
					!disableSort && (
						<SortIndicator
							indicatorIcon={sortInactive}
							sortDirection={sortKey}
						/>
					)
				)}
			</TableCell>
		);
	};

	const content =
		isFourColumns || isNotSmallScreen ? (
			(isItemTab && (
				<ColumnGroupingTable
					columns={columns}
					data={data}
					valueUnit={valueUnit}
					selectedTab={selectedTab}
					handleRowClick={handleRowClick}
					height={height}
					fontSize={fontSize}
					paddingRight={paddingRight}
					paddingLeft={paddingLeft}
					subHeaders={subHeaders}
					marginLeft={marginLeft}
					numberOfRecords={numberOfRecords}
					isStore={isStore}
				/>
			)) || (
				<StyledTable
					key="virtualized-table"
					height={height}
					width={width}
					headerHeight={headerHeight}
					className={classes.table}
					{...tableProps}
					sort={sort}
					sortBy={sortByDataKey}
					sortDirection={dataSortDirection}
					rowClassName={classes.rows}
					headerClassName={classes.header}
					deferredMeasurementCache={cache}
					rowHeight={rowHeight}
					rowCount={sortedList?.length}
					rowGetter={({ index }) => sortedList[index]}>
					{columns?.map((column, index) => {
						const { dataKey, ...other } = column;
						return (
							<Column
								key={dataKey}
								headerRenderer={(headerProps) =>
									headerRenderer({
										...headerProps,
										columnIndex: index,
									})
								}
								className={classes.flexContainer}
								cellRenderer={cellRenderer}
								dataKey={dataKey}
								{...other}
							/>
						);
					})}
				</StyledTable>
			)
		) : (
			<List
				className={classes.list}
				height={height}
				width={(isItemTab && '200') || width}
				rowHeight={smallScreenRowHeight}
				key="virtualized-list"
				overscanRowCount={(isItemTab && data?.length) || sortedList?.length}
				rowCount={(isItemTab && data?.length) || sortedList?.length}
				rowRenderer={rowRenderer}
			/>
		);

	return Object.keys(data)?.length !== 0 ? content : <></>;
};

VirtualizedTable.propTypes = {
	columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
	headerHeight: PropTypes.number,
	rowHeight: PropTypes.number,
	smallScreenRowHeight: PropTypes.number,
	data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
	height: PropTypes.number.isRequired,
	width: PropTypes.number.isRequired,
	initialSortKey: PropTypes.string,
	handleRowClick: PropTypes.func.isRequired,
	externalSortClick: PropTypes.bool.isRequired,
	isOnHandInventory: PropTypes.bool,
	isItemTab: PropTypes.bool,
	isTabItem: PropTypes.bool,
	valueUnit: PropTypes.string,
	selectedTab: PropTypes.string,
	isFourColumns: PropTypes.bool,
	fontSize: PropTypes.string,
	paddingLeft: PropTypes.string,
	paddingRight: PropTypes.string,
	subHeaders: PropTypes.bool,
	marginLeft: PropTypes.bool,
	numberOfRecords: PropTypes.number,
	isStore: PropTypes.bool,
};

VirtualizedTable.defaultProps = {
	headerHeight: 35,
	rowHeight: 46,
	smallScreenRowHeight: 78,
	initialSortKey: '',
	isItemTab: false,
	isTabItem: false,
	isFourColumns: false,
	fontSize: '16px',
	paddingLeft: '16px',
	paddingRight: '16px',
	subHeaders: false,
	marginLeft: '6px',
};

export default VirtualizedTable;
